import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, getDocs, doc, updateDoc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Correct collection path for bugReports
const BUGS_COLLECTION_REF = collection(db, 'gameTesting/bugReports/bugReports');

// Async thunks
export const fetchAssignedBugs = createAsyncThunk(
  'bugsAssigned/fetchAssignedBugs',
  async ({ assignedToEmail, filters = {} }, { rejectWithValue }) => {
    try {
      const itemsPerPage = 10;
      
      // Create base query to fetch bugs assigned to the user
      let bugsQuery = query(
        BUGS_COLLECTION_REF,
        where('assignedTo.email', '==', assignedToEmail),
        orderBy('lastUpdated', 'desc'),
        limit(itemsPerPage * 3) // Fetch more to account for client-side filtering
      );
      
      const snapshot = await getDocs(bugsQuery);
      
      // Convert the docs to data and apply client-side filtering
      let bugs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters client-side to avoid index requirements
      if (filters.status && filters.status !== 'all') {
        bugs = bugs.filter(bug => bug.status === filters.status);
      }
      
      if (filters.sensitivity && filters.sensitivity !== 'all') {
        bugs = bugs.filter(bug => bug.sensitivity === filters.sensitivity);
      }
      
      if (filters.gameName) {
        bugs = bugs.filter(bug => bug.gameName === filters.gameName);
      }
      
      // Limit to requested page size after filtering
      bugs = bugs.slice(0, itemsPerPage);
      
      // Get the last visible document for pagination
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Check if there are more bugs to fetch
      let hasMore = false;
      if (lastDoc && bugs.length === itemsPerPage) {
        const nextQuery = query(
          BUGS_COLLECTION_REF,
          where('assignedTo.email', '==', assignedToEmail),
          orderBy('lastUpdated', 'desc'),
          startAfter(lastDoc),
          limit(1)
        );
        const nextSnapshot = await getDocs(nextQuery);
        hasMore = !nextSnapshot.empty;
      }
      
      return {
        bugs,
        lastVisible: lastDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching assigned bugs:', error);
      return rejectWithValue(error.message || 'Failed to fetch assigned bugs');
    }
  }
);

export const loadMoreAssignedBugs = createAsyncThunk(
  'bugsAssigned/loadMoreAssignedBugs',
  async ({ assignedToEmail, filters = {} }, { getState, rejectWithValue }) => {
    try {
      const { bugsAssigned } = getState();
      const { lastVisible } = bugsAssigned;
      
      if (!lastVisible) {
        return { bugs: [], lastVisible: null, hasMore: false };
      }
      
      const itemsPerPage = 10;
      
      const moreQuery = query(
        BUGS_COLLECTION_REF,
        where('assignedTo.email', '==', assignedToEmail),
        orderBy('lastUpdated', 'desc'),
        startAfter(lastVisible),
        limit(itemsPerPage * 2)
      );
      
      const snapshot = await getDocs(moreQuery);
      
      let bugs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters client-side
      if (filters.status && filters.status !== 'all') {
        bugs = bugs.filter(bug => bug.status === filters.status);
      }
      
      if (filters.sensitivity && filters.sensitivity !== 'all') {
        bugs = bugs.filter(bug => bug.sensitivity === filters.sensitivity);
      }
      
      if (filters.gameName) {
        bugs = bugs.filter(bug => bug.gameName === filters.gameName);
      }
      
      bugs = bugs.slice(0, itemsPerPage);
      
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Check if there are more bugs
      let hasMore = false;
      if (lastDoc && bugs.length === itemsPerPage) {
        const nextQuery = query(
          BUGS_COLLECTION_REF,
          where('assignedTo.email', '==', assignedToEmail),
          orderBy('lastUpdated', 'desc'),
          startAfter(lastDoc),
          limit(1)
        );
        const nextSnapshot = await getDocs(nextQuery);
        hasMore = !nextSnapshot.empty;
      }
      
      return {
        bugs,
        lastVisible: lastDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error loading more assigned bugs:', error);
      return rejectWithValue(error.message || 'Failed to load more assigned bugs');
    }
  }
);

export const resetAssignedBugs = createAsyncThunk(
  'bugsAssigned/resetAssignedBugs',
  async () => {
    return true;
  }
);

export const updateBugStatus = createAsyncThunk(
  'bugsAssigned/updateBugStatus',
  async ({ bugId, status, updatedBy }, { rejectWithValue }) => {
    try {
      const bugRef = doc(BUGS_COLLECTION_REF, bugId);
      
      await updateDoc(bugRef, {
        status,
        lastUpdated: new Date(),
        updatedBy
      });
      
      return {
        bugId,
        status,
        lastUpdated: new Date(),
        updatedBy
      };
    } catch (error) {
      console.error('Error updating bug status:', error);
      return rejectWithValue(error.message || 'Failed to update bug status');
    }
  }
);

export const updateBugNotes = createAsyncThunk(
  'bugsAssigned/updateBugNotes',
  async ({ bugId, notes, updatedBy }, { rejectWithValue }) => {
    try {
      const bugRef = doc(BUGS_COLLECTION_REF, bugId);
      
      await updateDoc(bugRef, {
        notes,
        lastUpdated: new Date(),
        updatedBy
      });
      
      return {
        bugId,
        notes,
        lastUpdated: new Date(),
        updatedBy
      };
    } catch (error) {
      console.error('Error updating bug notes:', error);
      return rejectWithValue(error.message || 'Failed to update bug notes');
    }
  }
);

const initialState = {
  bugs: [],
  loading: false,
  error: null,
  hasMore: false,
  lastVisible: null,
  selectedBug: null
};

const bugsAssignedSlice = createSlice({
  name: 'bugsAssigned',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearSelectedBug: (state) => {
      state.selectedBug = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchAssignedBugs
      .addCase(fetchAssignedBugs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignedBugs.fulfilled, (state, action) => {
        state.bugs = action.payload.bugs;
        state.lastVisible = action.payload.lastVisible;
        state.hasMore = action.payload.hasMore;
        state.loading = false;
      })
      .addCase(fetchAssignedBugs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch assigned bug reports';
      })
      
      // loadMoreAssignedBugs
      .addCase(loadMoreAssignedBugs.pending, (state) => {
        // We don't set loading=true here as we'll use a separate loadingMore state in the component
      })
      .addCase(loadMoreAssignedBugs.fulfilled, (state, action) => {
        // Append new bugs
        state.bugs = [...state.bugs, ...action.payload.bugs];
        state.lastVisible = action.payload.lastVisible;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(loadMoreAssignedBugs.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch more assigned bug reports';
      })
      
      // resetAssignedBugs
      .addCase(resetAssignedBugs.fulfilled, (state) => {
        state.bugs = [];
        state.lastVisible = null;
        state.hasMore = false;
        state.error = null;
      })
      
      // updateBugStatus
      .addCase(updateBugStatus.fulfilled, (state, action) => {
        const { bugId, status, lastUpdated, updatedBy } = action.payload;
        
        // Update the bug in the list
        state.bugs = state.bugs.map(bug => 
          bug.id === bugId 
            ? { ...bug, status, lastUpdated, updatedBy } 
            : bug
        );
        
        // Update selected bug if it's the one we modified
        if (state.selectedBug && state.selectedBug.id === bugId) {
          state.selectedBug = {
            ...state.selectedBug,
            status,
            lastUpdated,
            updatedBy
          };
        }
      })
      
      // updateBugNotes
      .addCase(updateBugNotes.fulfilled, (state, action) => {
        const { bugId, notes, lastUpdated, updatedBy } = action.payload;
        
        // Update the bug in the list
        state.bugs = state.bugs.map(bug => 
          bug.id === bugId 
            ? { ...bug, notes, lastUpdated, updatedBy } 
            : bug
        );
        
        // Update selected bug if it's the one we modified
        if (state.selectedBug && state.selectedBug.id === bugId) {
          state.selectedBug = {
            ...state.selectedBug,
            notes,
            lastUpdated,
            updatedBy
          };
        }
      });
  }
});

export const { clearErrors, clearSelectedBug } = bugsAssignedSlice.actions;

export default bugsAssignedSlice.reducer;